import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { DeletedUserBadge } from "@/components/DeletedUserBadge";
import { toast } from "@/components/Toast";
import { useAuth } from "@/contexts/AuthContext";
import { ArrowLeft, CheckSquare, ClipboardList, Plus, Trash2, ChevronDown, ChevronUp, Camera } from "lucide-react";

const SCOPE_LABEL = { general: "General (todos los objetivos)", client: "Por objetivo", site: "Por sede" };

export default function ChecklistServicio() {
  const { user } = useAuth();
  const backLink = user?.role === "admin" ? "/admin" : "/supervisor";
  const [tab, setTab] = useState("templates");
  const [clients, setClients] = useState([]);

  useEffect(() => { (async () => {
    try { const c = await api.get("/clients"); setClients(c.data); } catch (e) { toast.error(formatApiError(e)); }
  })(); }, []);

  return (
    <div style={{ padding: "20px 32px", maxWidth: 1100, margin: "0 auto" }} data-testid="checklist-servicio-page">
      <Link to={backLink} style={{ display: "inline-flex", alignItems: "center", gap: 6, color: "var(--invict-text-muted)", fontSize: 12, marginBottom: 12, textDecoration: "none", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em" }}><ArrowLeft size={14} /> Volver</Link>
      <h1 style={{ fontSize: 26, fontWeight: 900, margin: "4px 0 4px", color: "#0A0A0A", letterSpacing: "-0.02em" }}>Checklist de Servicio</h1>
      <p style={{ fontSize: 12, color: "var(--invict-text-muted)", marginBottom: 18 }}>
        Ítems que el personal completa al iniciar el turno en el puesto. Distinto de "Elementos de Trabajo" (kit personal del vigilador).
      </p>

      <div style={{ display: "flex", gap: 8, marginBottom: 18 }}>
        <TabBtn active={tab === "templates"} onClick={() => setTab("templates")} Ic={ClipboardList} label="Ítems del checklist" testid="checklist-tab-templates" />
        <TabBtn active={tab === "completions"} onClick={() => setTab("completions")} Ic={CheckSquare} label="Checklists completados" testid="checklist-tab-completions" />
      </div>

      {tab === "templates" ? <TemplatesModule clients={clients} /> : <CompletionsModule clients={clients} />}
    </div>
  );
}

function TabBtn({ active, onClick, Ic, label, testid }) {
  return (
    <button type="button" onClick={onClick} data-testid={testid}
      style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "11px 18px", borderRadius: 8,
        border: "none", background: active ? "#0A0A0A" : "#fff", color: active ? "#FFFBF4" : "#0A0A0A",
        fontWeight: 800, fontSize: 13, cursor: "pointer", boxShadow: "0 1px 3px rgba(0,0,0,0.08)" }}>
      <Ic size={16} /> {label}
    </button>
  );
}

/* ============================ PLANTILLAS ============================ */
function TemplatesModule({ clients }) {
  const [templates, setTemplates] = useState(null);
  const [open, setOpen] = useState(false);
  const [scope, setScope] = useState("general");
  const [clientId, setClientId] = useState("");
  const [siteId, setSiteId] = useState("");
  const [sites, setSites] = useState([]);
  const [items, setItems] = useState([""]);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try { const { data } = await api.get("/checklist/templates"); setTemplates(data); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (scope !== "site" || !clientId) { setSites([]); return; }
    (async () => { try { const { data } = await api.get(`/clients/${clientId}/sites`); setSites(data); } catch { setSites([]); } })();
  }, [scope, clientId]);

  const resetForm = () => { setScope("general"); setClientId(""); setSiteId(""); setItems([""]); setOpen(false); };

  const submit = async (e) => {
    e.preventDefault();
    const cleanItems = items.map((i) => i.trim()).filter(Boolean);
    if (cleanItems.length === 0) return toast.error("Agregá al menos un ítem");
    if (scope !== "general" && !clientId) return toast.error("Elegí un objetivo");
    if (scope === "site" && !siteId) return toast.error("Elegí una sede");
    setBusy(true);
    try {
      await api.post("/checklist/templates", { scope, client_id: scope !== "general" ? clientId : null, site_id: scope === "site" ? siteId : null, items: cleanItems });
      toast.success("Checklist creado");
      resetForm();
      load();
    } catch (e2) { toast.error(formatApiError(e2)); }
    finally { setBusy(false); }
  };

  const toggleActive = async (t) => {
    try { await api.patch(`/checklist/templates/${t.id}`, { active: !t.active }); load(); } catch (e) { toast.error(formatApiError(e)); }
  };
  const removeTemplate = async (t) => {
    if (!window.confirm("¿Eliminar este checklist?")) return;
    try { await api.delete(`/checklist/templates/${t.id}`); load(); toast.success("Eliminado"); } catch (e) { toast.error(formatApiError(e)); }
  };
  const clientName = (cid) => clients.find((c) => c.id === cid)?.name || "Objetivo eliminado";

  return (
    <div>
      {!open && <button className="btn-primary" onClick={() => setOpen(true)} data-testid="checklist-new-template-btn"><Plus size={14} style={{ marginRight: 6 }} />Nuevo checklist</button>}

      {open && (
        <form onSubmit={submit} className="card" style={{ marginTop: 12, marginBottom: 20 }} data-testid="checklist-new-template-form">
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 12 }}>
            <div className="field" style={{ margin: 0 }}>
              <label>Alcance</label>
              <select value={scope} onChange={(e) => setScope(e.target.value)} data-testid="checklist-form-scope">
                <option value="general">General (todos los objetivos)</option>
                <option value="client">Por objetivo</option>
                <option value="site">Por sede</option>
              </select>
            </div>
            {scope !== "general" && (
              <div className="field" style={{ margin: 0 }}>
                <label>Objetivo</label>
                <select value={clientId} onChange={(e) => { setClientId(e.target.value); setSiteId(""); }} data-testid="checklist-form-client">
                  <option value="">Elegir...</option>
                  {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
            )}
            {scope === "site" && (
              <div className="field" style={{ margin: 0 }}>
                <label>Sede</label>
                <select value={siteId} onChange={(e) => setSiteId(e.target.value)} data-testid="checklist-form-site">
                  <option value="">Elegir...</option>
                  {sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
            )}
          </div>

          <label style={{ display: "block", marginBottom: 6, fontSize: 12, fontWeight: 700 }}>Ítems (texto libre)</label>
          {items.map((it, i) => (
            <div key={i} style={{ display: "flex", gap: 6, marginBottom: 6 }}>
              <input value={it} onChange={(e) => setItems(items.map((v, idx) => idx === i ? e.target.value : v))} placeholder={`Ítem ${i + 1}`} data-testid={`checklist-form-item-${i}`} style={{ flex: 1 }} />
              {items.length > 1 && <button type="button" className="btn-ghost" onClick={() => setItems(items.filter((_, idx) => idx !== i))}><Trash2 size={13} /></button>}
            </div>
          ))}
          <button type="button" className="btn-ghost" onClick={() => setItems([...items, ""])} data-testid="checklist-form-add-item" style={{ marginBottom: 14 }}><Plus size={12} style={{ marginRight: 4 }} />Agregar ítem</button>

          <div style={{ display: "flex", gap: 8 }}>
            <button type="submit" className="btn-primary" disabled={busy} data-testid="checklist-form-submit">{busy ? "Guardando..." : "Crear checklist"}</button>
            <button type="button" className="btn-ghost" onClick={resetForm}>Cancelar</button>
          </div>
        </form>
      )}

      {templates == null ? (
        <div className="skel" style={{ height: 80, marginTop: 12 }} />
      ) : templates.length === 0 ? (
        <div className="card" style={{ textAlign: "center", padding: 30, color: "var(--invict-text-muted)", marginTop: 12 }}>Sin checklists definidos todavía.</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 12 }}>
          {templates.map((t) => (
            <div key={t.id} className="card" style={{ opacity: t.active ? 1 : 0.5 }} data-testid={`checklist-template-${t.id}`}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
                <div>
                  <span style={{ fontSize: 10, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.06em", color: "#BF3437" }}>{SCOPE_LABEL[t.scope]}</span>
                  {t.client_id && <div style={{ fontSize: 13, fontWeight: 800, color: "#0A0A0A" }}>{clientName(t.client_id)}</div>}
                </div>
                <div style={{ display: "flex", gap: 6 }}>
                  <button className="btn-ghost" onClick={() => toggleActive(t)} data-testid={`checklist-toggle-${t.id}`} style={{ fontSize: 11 }}>{t.active ? "Desactivar" : "Activar"}</button>
                  <button className="btn-ghost" onClick={() => removeTemplate(t)} data-testid={`checklist-delete-${t.id}`} style={{ color: "#BF3437" }}><Trash2 size={13} /></button>
                </div>
              </div>
              <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, color: "var(--invict-text-muted)" }}>
                {t.items.map((it) => <li key={it.id}>{it.text}</li>)}
              </ul>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ============================ COMPLETADOS ============================ */
function CompletionsModule({ clients }) {
  const [clientId, setClientId] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [rows, setRows] = useState(null);
  const [expanded, setExpanded] = useState(null);

  const load = async () => {
    try {
      const params = {};
      if (clientId) params.client_id = clientId;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const { data } = await api.get("/checklist/completions", { params });
      setRows(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, [clientId, dateFrom, dateTo]);

  return (
    <div>
      <div className="card" style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 14 }}>
        <div className="field" style={{ margin: 0 }}>
          <label>Objetivo</label>
          <select value={clientId} onChange={(e) => setClientId(e.target.value)} data-testid="checklist-filter-client">
            <option value="">Todos</option>
            {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
        <div className="field" style={{ margin: 0 }}><label>Desde</label><input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="checklist-filter-from" /></div>
        <div className="field" style={{ margin: 0 }}><label>Hasta</label><input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="checklist-filter-to" /></div>
      </div>

      {rows == null ? (
        <div className="skel" style={{ height: 80 }} />
      ) : rows.length === 0 ? (
        <div className="card" style={{ textAlign: "center", padding: 30, color: "var(--invict-text-muted)" }}>Sin checklists completados en este rango.</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }} data-testid="checklist-completions-list">
          {rows.map((r) => {
            const okCount = r.items.filter((i) => i.completed).length;
            const isOpen = expanded === r.id;
            return (
              <div key={r.id} className="card" data-testid={`checklist-completion-${r.id}`}>
                <button onClick={() => setExpanded(isOpen ? null : r.id)} style={{ width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center", background: "none", border: "none", cursor: "pointer", padding: 0, textAlign: "left" }}>
                  <div>
                    <div style={{ fontWeight: 800, fontSize: 13, color: "#0A0A0A" }}>{r.client_name}{r.site_name ? ` · ${r.site_name}` : ""}</div>
                    <div style={{ fontSize: 11, color: "var(--invict-text-muted)" }}>{r.user_name}{r.user_deleted && <DeletedUserBadge id={r.id} />} · {new Date(r.completed_at).toLocaleString("es-AR")}</div>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ fontSize: 12, fontWeight: 800, color: okCount === r.items.length ? "#16a34a" : "#d97706" }}>{okCount}/{r.items.length}</span>
                    {isOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                  </div>
                </button>
                {isOpen && (
                  <div style={{ marginTop: 10, paddingTop: 10, borderTop: "1px solid var(--invict-bone-3)" }}>
                    {r.items.map((it, i) => (
                      <div key={i} style={{ display: "flex", gap: 8, alignItems: "flex-start", padding: "5px 0", fontSize: 12 }}>
                        <span style={{ fontWeight: 800, color: it.completed ? "#16a34a" : "#BF3437", minWidth: 70 }}>{it.completed ? "Cumplido" : "No cumplido"}</span>
                        <span style={{ flex: 1 }}>{it.text}{it.observation && <span style={{ color: "var(--invict-text-muted)" }}> — {it.observation}</span>}</span>
                        {it.photo_url && <a href={it.photo_url.startsWith("http") ? it.photo_url : `${process.env.REACT_APP_BACKEND_URL}${it.photo_url}`} target="_blank" rel="noreferrer"><Camera size={14} /></a>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
